import React, { useEffect, useMemo, useState } from "react";
import { Building, Check, ChevronRight, Loader2, Users, X } from "lucide-react";

/**
 * CustomerPickerModal
 *
 * Used in two flows:
 *  - mode='initial'  — shown on login when a login is linked to >1 orgs. The
 *                       user MUST pick one (modal is non-dismissable).
 *  - mode='switch'   — opened from the topbar dropdown. Highlights the
 *                       currently active link via `currentLinkId` and is
 *                       dismissable via Cancel / Esc / backdrop click.
 *
 * Props:
 *   isOpen        boolean
 *   onClose       () => void       (no-op in 'initial' mode)
 *   links         Array<Link>      from GET /login/identifier-links
 *   currentLinkId number?          (switch mode only)
 *   mode          'initial'|'switch'
 *   onConfirm     ({ linkId, remember }) => Promise<void>|void
 *   persona       string?          ('STAFF', 'DEVELOPER_ADMIN', ...)
 */
const personaLabel = (persona) => {
  if (!persona) return "Member";
  return persona
    .toString()
    .split("_")
    .map((p) => p[0] + p.slice(1).toLowerCase())
    .join(" ");
};

const linkOrgName = (link) => {
  const d = link?.identifierDetails || {};
  // Persona schemas place company_name either at the top level (user /
  // interior designer / channel partner) or nested under `customer` (sales
  // associate). Fall back through the common shapes.
  return (
    d.customer?.companyName ||
    d.companyName ||
    d.name ||
    d.customer?.name ||
    "Untitled organization"
  );
};

const linkOrgSubtitle = (link) => {
  const d = link?.identifierDetails || {};
  return (
    d.customer?.email ||
    d.email ||
    d.customer?.mobileNoWa ||
    d.mobileNoWa ||
    ""
  );
};

const linkOrgInitial = (link) => {
  const name = linkOrgName(link);
  return (name || "?").trim().charAt(0).toUpperCase();
};

const CustomerPickerModal = ({
  isOpen,
  onClose,
  links = [],
  currentLinkId,
  mode = "initial",
  onConfirm,
  persona,
}) => {
  const isInitial = mode === "initial";

  // Sensible default selection: the active link in switch mode, the primary
  // (or first) link in initial mode.
  const defaultLinkId = useMemo(() => {
    if (!links?.length) return null;
    if (!isInitial && currentLinkId) return currentLinkId;
    const primary = links.find((l) => l.isPrimary);
    return (primary || links[0])?.linkId ?? null;
  }, [links, currentLinkId, isInitial]);

  const [selectedLinkId, setSelectedLinkId] = useState(defaultLinkId);
  const [remember, setRemember] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Sync default when modal re-opens with a new link set.
  useEffect(() => {
    if (isOpen) {
      setSelectedLinkId(defaultLinkId);
      setRemember(false);
      setSubmitting(false);
    }
  }, [isOpen, defaultLinkId]);

  // ESC handler — only fires in switch mode; initial mode swallows it.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (e.key === "Escape" && !isInitial && !submitting) {
        onClose?.();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, isInitial, onClose, submitting]);

  if (!isOpen) return null;

  const title = isInitial ? "Choose your organization" : "Switch organization";
  const subtitle = isInitial
    ? "This account is linked to multiple organizations. Pick the one you want to work in."
    : "Switch which organization this session is scoped to. You'll see only that org's data after switching.";

  const handleBackdropClick = () => {
    if (isInitial || submitting) return;
    onClose?.();
  };

  const handleConfirm = async () => {
    if (!selectedLinkId || submitting) return;
    try {
      setSubmitting(true);
      await onConfirm?.({ linkId: selectedLinkId, remember: isInitial ? remember : false });
    } finally {
      // Parent typically unmounts us after a successful confirm; this is a
      // safety net for error paths so the button doesn't stay stuck.
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="customer-picker-title"
      onClick={handleBackdropClick}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Slate header */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-800 to-slate-600 px-6 py-5 sm:py-6">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 0%, rgba(255,255,255,0.6) 0, transparent 50%)",
            }}
          />
          <div className="relative flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-white/80 backdrop-blur-sm">
                <Users size={11} />
                {personaLabel(persona)}
              </div>
              <h2
                id="customer-picker-title"
                className="mt-2.5 text-lg sm:text-xl font-semibold leading-tight text-white"
              >
                {title}
              </h2>
              <p className="mt-1.5 text-xs leading-relaxed text-white/70">{subtitle}</p>
            </div>
            {!isInitial && (
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="shrink-0 rounded-lg p-1.5 text-white/80 hover:bg-white/10 hover:text-white transition-colors disabled:opacity-50"
                aria-label="Close switcher"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Body — list of org cards */}
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {(!links || links.length === 0) ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Building className="text-slate-300" size={42} />
              <p className="mt-3 text-sm text-slate-500">
                No organizations are linked to this login.
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {links.map((link) => {
                const active = link.linkId === selectedLinkId;
                const isCurrent = !isInitial && link.linkId === currentLinkId;
                return (
                  <li key={link.linkId}>
                    <button
                      type="button"
                      onClick={() => setSelectedLinkId(link.linkId)}
                      className={`group flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
                        active
                          ? "border-flamingo-500 bg-flamingo-50/50 ring-1 ring-flamingo-200"
                          : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      {/* Avatar / initials */}
                      <div
                        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-base font-semibold ${
                          active
                            ? "bg-flamingo-500 text-white"
                            : "bg-slate-100 text-slate-600 group-hover:bg-slate-200"
                        }`}
                        aria-hidden
                      >
                        {linkOrgInitial(link)}
                      </div>
                      {/* Org name + meta */}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="truncate text-sm font-semibold text-slate-900">
                            {linkOrgName(link)}
                          </span>
                          {isCurrent && (
                            <span className="inline-flex items-center rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">
                              Active
                            </span>
                          )}
                          <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                            {personaLabel(persona)}
                          </span>
                        </div>
                        {linkOrgSubtitle(link) && (
                          <p className="mt-0.5 truncate text-xs text-slate-500">
                            {linkOrgSubtitle(link)}
                          </p>
                        )}
                      </div>
                      {/* Selected indicator */}
                      <div
                        className={`shrink-0 ${
                          active ? "text-flamingo-500" : "text-slate-300 group-hover:text-slate-400"
                        }`}
                      >
                        {active ? <Check size={20} /> : <ChevronRight size={18} />}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer — remember + actions */}
        <div className="border-t border-slate-200 bg-slate-50/60 px-6 py-4">
          {isInitial && (
            <label className="mb-3 flex cursor-pointer items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-flamingo-500 focus:ring-flamingo-500"
              />
              Remember this choice for future logins
            </label>
          )}
          <div className="flex items-center justify-end gap-2">
            {!isInitial && (
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!selectedLinkId || submitting}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-br from-flamingo-500 to-flamingo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:from-flamingo-600 hover:to-flamingo-700 focus:outline-none focus:ring-2 focus:ring-flamingo-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting && (
                <Loader2 size={14} className="animate-spin" aria-hidden />
              )}
              {submitting
                ? isInitial
                  ? "Working…"
                  : "Switching…"
                : isInitial
                  ? "Continue"
                  : "Switch"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerPickerModal;
